import { useState } from "react";
import { ArrowDown, ArrowUp, ImagePlus, Loader2, Plus, Star, Trash2, X } from "lucide-react";
import { api, imgUrl } from "@/lib/api";
import { CATEGORIES } from "@/data/siteData";
import ImageField from "./ImageField";
import MediaPicker from "./MediaPicker";

const labelCls = "mb-2 block text-[11px] font-semibold uppercase tracking-[0.2em] text-[#5C5C5C]";
const inputCls =
  "w-full rounded-xl border border-line bg-cream/50 px-4 py-3 text-sm text-ink outline-none transition-colors focus:border-rose";

export default function ProductForm({ product, categories, onClose, onSaved }) {
  const isEdit = Boolean(product);
  const categoryOptions = categories?.length
    ? categories
    : CATEGORIES.map((c) => ({ slug: c.id, name: c.name }));

  const initialImages = Array.isArray(product?.images) && product.images.length
    ? product.images.map((i) => i.path)
    : product?.image
      ? [product.image]
      : [];

  const [form, setForm] = useState({
    name: product?.name || "",
    price: product?.price || "",
    description: product?.description || "",
    long_description: product?.long_description || "",
    details: Array.isArray(product?.details) ? product.details.join("\n") : "",
    featured: Boolean(product?.featured),
    active: product ? Boolean(product.active) : true,
  });
  const [images, setImages] = useState(initialImages);
  const [cats, setCats] = useState(
    Array.isArray(product?.categories) && product.categories.length
      ? product.categories
      : product?.category
        ? [product.category]
        : [categoryOptions[0]?.slug || ""].filter(Boolean),
  );
  const [video, setVideo] = useState(product?.video || "");
  const [urlDraft, setUrlDraft] = useState("");
  const [picker, setPicker] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadImage = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    setError("");
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("image", file);
        const { data } = await api.post("/admin/upload", fd);
        setImages((list) => [...list, data.path]);
      } catch (err) {
        const detail = err.response?.data?.detail;
        setError((Array.isArray(detail) ? detail[0]?.msg : detail) || "Image upload failed (JPG/PNG/WebP, max 5 MB).");
      }
    }
    setUploading(false);
  };

  const moveImage = (i, dir) => {
    setImages((list) => {
      const next = [...list];
      const j = i + dir;
      if (j < 0 || j >= next.length) return list;
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  const addUrl = () => {
    const u = urlDraft.trim();
    if (u) setImages((list) => [...list, u]);
    setUrlDraft("");
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.price.trim()) {
      setError("Name and price are required.");
      return;
    }
    if (!cats.length) {
      setError("Select at least one category.");
      return;
    }
    setSaving(true);
    setError("");
    const payload = {
      name: form.name.trim(),
      category: cats[0],
      categories: cats,
      price: form.price.trim(),
      description: form.description,
      long_description: form.long_description,
      details: form.details.split("\n").map((l) => l.trim()).filter(Boolean),
      image: images[0] || "",
      images: images.map((path) => ({ path })),
      video,
      featured: form.featured,
      active: form.active,
    };
    try {
      if (isEdit) await api.patch(`/admin/products/${product.id}`, payload);
      else await api.post("/admin/products", payload);
      onSaved();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError((Array.isArray(detail) ? detail[0]?.msg : detail) || "Could not save the product.");
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center md:items-center md:p-8">
      <div className="absolute inset-0 bg-ink/60 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div
        data-testid="product-form-modal"
        role="dialog"
        aria-modal="true"
        aria-label={isEdit ? "Edit product" : "Add product"}
        className="relative max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-t-3xl bg-white p-6 md:rounded-3xl md:p-8"
      >
        <div className="mb-6 flex items-center justify-between">
          <h3 className="font-serif text-2xl font-medium">
            {isEdit ? "Edit Product" : "Add Product"}
          </h3>
          <button
            data-testid="product-form-close"
            onClick={onClose}
            aria-label="Close"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-line transition-colors hover:bg-ink hover:text-cream"
          >
            <X size={16} />
          </button>
        </div>

        <form data-testid="product-form" onSubmit={save} className="flex flex-col gap-5">
          <div>
            <span className={labelCls}>Product Images (first image = main/primary)</span>
            <div data-testid="product-images-list" className="flex flex-wrap gap-3">
              {images.map((path, i) => (
                <div key={path + i} className="relative">
                  <div className={`h-20 w-20 overflow-hidden rounded-xl border-2 ${i === 0 ? "border-rose" : "border-line"}`}>
                    <img src={imgUrl(path)} alt="" className="h-full w-full object-cover" />
                  </div>
                  {i === 0 && (
                    <span className="absolute -top-2 left-1/2 flex -translate-x-1/2 items-center gap-0.5 whitespace-nowrap rounded-full bg-rose px-2 py-0.5 text-[9px] font-bold uppercase text-white">
                      <Star size={8} className="fill-white" /> Main
                    </span>
                  )}
                  <div className="mt-1.5 flex justify-center gap-1">
                    <button type="button" onClick={() => moveImage(i, -1)} disabled={i === 0} aria-label="Move image left" data-testid={`image-left-${i}`} className="rounded-full border border-line p-1 hover:border-rose hover:text-rose disabled:opacity-30"><ArrowDown size={11} className="rotate-90" /></button>
                    <button type="button" onClick={() => moveImage(i, 1)} disabled={i === images.length - 1} aria-label="Move image right" data-testid={`image-right-${i}`} className="rounded-full border border-line p-1 hover:border-rose hover:text-rose disabled:opacity-30"><ArrowUp size={11} className="rotate-90" /></button>
                    <button type="button" onClick={() => setImages((list) => list.filter((_, x) => x !== i))} aria-label="Remove image" data-testid={`image-remove-${i}`} className="rounded-full border border-line p-1 hover:border-red-400 hover:text-red-500"><Trash2 size={11} /></button>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <label
                data-testid="product-image-upload-label"
                className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-ink px-4 py-2.5 text-xs font-medium transition-colors hover:bg-ink hover:text-cream ${uploading ? "pointer-events-none opacity-60" : ""}`}
              >
                {uploading ? <Loader2 size={13} className="animate-spin" /> : <ImagePlus size={13} />}
                {uploading ? "Uploading…" : "Upload images"}
                <input data-testid="product-image-input" type="file" multiple accept="image/jpeg,image/png,image/webp" onChange={uploadImage} className="hidden" />
              </label>
              <button type="button" data-testid="product-image-media" onClick={() => setPicker(true)} className="inline-flex items-center gap-1.5 rounded-full border border-line px-4 py-2.5 text-xs font-medium transition-colors hover:border-rose hover:text-rose">
                Choose from Media
              </button>
            </div>
            <div className="mt-2 flex gap-2">
              <input
                data-testid="product-image-url-input"
                type="text"
                value={urlDraft}
                onChange={(e) => setUrlDraft(e.target.value)}
                placeholder="…or paste an image URL"
                className={`${inputCls} flex-1`}
              />
              <button type="button" data-testid="product-image-add-url" onClick={addUrl} className="inline-flex items-center gap-1 rounded-full border border-line px-4 text-xs font-medium hover:border-rose hover:text-rose">
                <Plus size={13} /> Add
              </button>
            </div>
          </div>

          <ImageField label="Product Video (optional — MP4/WebM, shown in the gallery)" value={video} onChange={setVideo} kind="video" testid="product-video" />

          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="pf-name" className={labelCls}>Name *</label>
              <input id="pf-name" data-testid="product-name-input" type="text" value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} placeholder="e.g. Birthday Surprise Box" />
            </div>
            <div>
              <label htmlFor="pf-price" className={labelCls}>Price *</label>
              <input id="pf-price" data-testid="product-price-input" type="text" value={form.price} onChange={(e) => set("price", e.target.value)} className={inputCls} placeholder="e.g. ₹1,499" />
            </div>
          </div>

          <div>
            <span className={labelCls}>Categories (select one or more — first selected is the primary)</span>
            <div data-testid="product-categories-multiselect" className="flex flex-wrap gap-2">
              {categoryOptions.map((c) => {
                const slug = c.slug || c.id;
                const on = cats.includes(slug);
                return (
                  <button
                    type="button"
                    key={slug}
                    data-testid={`product-cat-${slug}`}
                    aria-pressed={on}
                    onClick={() => setCats((list) => (on ? list.filter((s) => s !== slug) : [...list, slug]))}
                    className={`rounded-full border px-4 py-2 text-xs font-medium transition-colors ${
                      on ? "border-ink bg-ink text-cream" : "border-line text-[#5C5C5C] hover:border-rose hover:text-rose"
                    }`}
                  >
                    {c.name}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label htmlFor="pf-desc" className={labelCls}>Short Description</label>
            <textarea id="pf-desc" data-testid="product-description-input" rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} className={`${inputCls} resize-none`} placeholder="One or two lines shown on the product card" />
          </div>

          <div>
            <label htmlFor="pf-long" className={labelCls}>Detailed Description</label>
            <textarea id="pf-long" data-testid="product-long-description-input" rows={2} value={form.long_description} onChange={(e) => set("long_description", e.target.value)} className={`${inputCls} resize-none`} placeholder="Shown in the product detail popup" />
          </div>

          <div>
            <label htmlFor="pf-details" className={labelCls}>What's Inside (one per line)</label>
            <textarea id="pf-details" data-testid="product-details-input" rows={3} value={form.details} onChange={(e) => set("details", e.target.value)} className={`${inputCls} resize-none`} placeholder={"Handwritten message card\nPremium packaging"} />
          </div>

          <div className="flex flex-wrap gap-6">
            <label className="flex cursor-pointer items-center gap-3 text-sm font-medium">
              <input data-testid="product-featured-toggle" type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} className="h-4 w-4 accent-[#C48B81]" />
              Featured (shows first)
            </label>
            <label className="flex cursor-pointer items-center gap-3 text-sm font-medium">
              <input data-testid="product-active-toggle" type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} className="h-4 w-4 accent-[#C48B81]" />
              Active (visible on website)
            </label>
          </div>

          {error && (
            <p data-testid="product-form-error" className="rounded-xl bg-red-50 px-4 py-3 text-xs font-medium text-red-600">
              {error}
            </p>
          )}

          <div className="flex gap-3 pt-2">
            <button
              data-testid="product-save-button"
              type="submit"
              disabled={saving || uploading}
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-ink px-6 py-4 text-sm font-medium text-cream transition-colors hover:bg-rose hover:text-white disabled:opacity-60"
            >
              {saving ? <><Loader2 size={15} className="animate-spin" /> Saving…</> : isEdit ? "Save Changes" : "Add Product"}
            </button>
            <button type="button" onClick={onClose} className="rounded-full border border-line px-6 py-4 text-sm font-medium transition-colors hover:border-ink">
              Cancel
            </button>
          </div>
        </form>
      </div>
      {picker && (
        <MediaPicker kind="image" onClose={() => setPicker(false)} onSelect={(path) => { setImages((list) => [...list, path]); setPicker(false); }} />
      )}
    </div>
  );
}
